import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import messagingApi from '../api/messagingApi';
import { Conversation, Message, LoadingStatus } from '../../types';

interface MessagingState {
  conversations: Conversation[];
  conversationsCursor: string | null;
  activeConversationId: string | null;
  messages: Record<string, Message[]>;
  unreadCount: number;
  status: LoadingStatus;
  error: string | null;
}

const initialState: MessagingState = {
  conversations: [],
  conversationsCursor: null,
  activeConversationId: null,
  messages: {},
  unreadCount: 0,
  status: 'idle',
  error: null,
};

export const fetchConversations = createAsyncThunk(
  'messaging/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const res = await messagingApi.getConversations();
      return res.data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load conversations');
    }
  },
);

export const fetchMessages = createAsyncThunk(
  'messaging/fetchMessages',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const res = await messagingApi.getMessages(conversationId);
      return { conversationId, messages: res.data.messages as Message[] };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load messages');
    }
  },
);

export const markConversationRead = createAsyncThunk(
  'messaging/markRead',
  async (conversationId: string) => {
    await messagingApi.markRead(conversationId);
    return conversationId;
  },
);

export const sendMessage = createAsyncThunk(
  'messaging/sendMessage',
  async ({ conversationId, content }: { conversationId: string; content: string }, { rejectWithValue }) => {
    try {
      const res = await messagingApi.sendMessage(conversationId, content);
      // res.data = { message, warning? }
      const payload = res.data as { message: Message; warning?: string };
      return { conversationId, message: payload.message, warning: payload.warning };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to send message');
    }
  },
);

export const sendVoiceMessage = createAsyncThunk(
  'messaging/sendVoiceMessage',
  async ({ conversationId, audioBlob, duration }: { conversationId: string; audioBlob: Blob; duration: number }, { rejectWithValue }) => {
    try {
      const res = await messagingApi.sendVoiceMessage(conversationId, audioBlob, duration);
      const payload = res.data as { message: Message };
      return { conversationId, message: payload.message };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to send voice message');
    }
  },
);

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {
    setActiveConversation(state, action: PayloadAction<string | null>) {
      state.activeConversationId = action.payload;
    },
    appendMessage(state, action: PayloadAction<{ conversationId: string; message: Message }>) {
      const { conversationId, message } = action.payload;
      if (!state.messages[conversationId]) state.messages[conversationId] = [];
      state.messages[conversationId].unshift(message);
    },
    updateMessage(state, action: PayloadAction<{ conversationId: string; message: Message }>) {
      const { conversationId, message } = action.payload;
      const list = state.messages[conversationId];
      if (!list) return;
      const idx = list.findIndex((m) => m._id === message._id);
      if (idx !== -1) list[idx] = message;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchConversations.fulfilled, (state, { payload }) => {
        state.conversations = payload.conversations;
        state.conversationsCursor = payload.meta.cursor;
        state.status = 'succeeded';
      })
      .addCase(fetchConversations.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(fetchMessages.fulfilled, (state, { payload }) => {
        state.messages[payload.conversationId] = payload.messages;
      })
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        if (!state.messages[payload.conversationId]) state.messages[payload.conversationId] = [];
        // Always add to sender's local state — held messages show with "under review" indicator
        state.messages[payload.conversationId].unshift(payload.message);
      })
      .addCase(sendVoiceMessage.fulfilled, (state, { payload }) => {
        if (!state.messages[payload.conversationId]) state.messages[payload.conversationId] = [];
        state.messages[payload.conversationId].unshift(payload.message);
      })
      .addCase(markConversationRead.fulfilled, (state, { payload: conversationId }) => {
        const convo = state.conversations.find((c) => c._id === conversationId);
        if (convo) convo.unreadCount = 0;
      });
  },
});

export const { setActiveConversation, appendMessage, updateMessage } = messagingSlice.actions;
export default messagingSlice.reducer;

export const selectConversations = (state: { messaging: MessagingState }) => state.messaging.conversations;
export const selectActiveConversationId = (state: { messaging: MessagingState }) => state.messaging.activeConversationId;
export const selectConversationById = (conversationId: string) =>
  (state: { messaging: MessagingState }) => state.messaging.conversations.find((c) => c._id === conversationId);
export const selectMessages = (conversationId: string) =>
  (state: { messaging: MessagingState }) => state.messaging.messages[conversationId] || [];
